(() => {
  const STORAGE_KEY = 'toku:subscription-bonus-configs';
  const PROPERTY_KEY = '_subscription_bonus_for';
  const FLAG_KEY = 'TOKU_SUBSCRIPTION_BONUS_ENABLED';

  if (typeof window[FLAG_KEY] === 'undefined') {
    window[FLAG_KEY] = false;
  }

  class SubscriptionBonusProducts {
    constructor() {
      this.configs = new Map();
      this.syncing = false;
      this.loadStoredConfigs();
      this.registerFromDocument();
      this.bindCartPageRemove();
    }

    isEnabled() {
      return this.isTestBActive() && this.isIncludedOrderVisible();
    }

    isTestBActive() {
      return Boolean(document.querySelector('.ab-test-supply-selector-widget'));
    }

    isIncludedOrderVisible() {
      const widget = document.querySelector('.ab-test-supply-selector-widget');
      if (!widget) return false;

      const widgetStyle = window.getComputedStyle(widget);
      if (widgetStyle.display === 'none' || widgetStyle.visibility === 'hidden') return false;

      const block = widget.querySelector('.loop-widget-included-order');
      if (!block) return false;

      const blockStyle = window.getComputedStyle(block);
      return blockStyle.display !== 'none' && blockStyle.visibility !== 'hidden';
    }

    loadStoredConfigs() {
      try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        if (!stored) return;

        JSON.parse(stored).forEach((config) => this.registerConfig(config, { persist: false }));
      } catch (error) {
        // Ignore invalid stored configs.
      }
    }

    persistConfigs() {
      try {
        const payload = Array.from(this.configs.values());
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (error) {
        // Ignore storage access errors.
      }
    }

    registerConfig(config, options = {}) {
      if (!config?.productId) return;

      const bonusVariantIds = (config.bonusVariantIds || [])
        .map((id) => String(id))
        .filter(Boolean);

      if (!bonusVariantIds.length) return;

      this.configs.set(String(config.productId), {
        productId: String(config.productId),
        bonusVariantIds,
        eligibleVariantIds: (config.eligibleVariantIds || []).map((id) => String(id)).filter(Boolean),
      });

      if (options.persist !== false) {
        this.persistConfigs();
      }
    }

    registerFromDocument() {
      document.querySelectorAll('[data-subscription-bonus-config]').forEach((node) => {
        try {
          this.registerConfig(JSON.parse(node.textContent));
        } catch (error) {
          console.error('Unable to parse subscription bonus config', error);
        }
      });
    }

    getConfigs() {
      return Array.from(this.configs.values());
    }

    getSellingPlanId(item) {
      return String(
        item?.selling_plan_allocation?.selling_plan?.id
          || item?.selling_plan_allocation?.selling_plan_id
          || item?.selling_plan
          || '',
      );
    }

    getLineProperty(item, key) {
      const properties = item?.properties;
      if (!properties) return '';

      if (Array.isArray(properties)) {
        const match = properties.find((property) => {
          if (!property) return false;
          if (Array.isArray(property)) return property[0] === key;
          return property.name === key || property.first === key || property.key === key;
        });

        if (!match) return '';
        if (Array.isArray(match)) return String(match[1] || '');
        return String(match.value || match.last || '');
      }

      if (typeof properties === 'object') {
        return properties[key] == null ? '' : String(properties[key]);
      }

      return '';
    }

    hasActiveSubscription(cart, productId) {
      return (cart?.items || []).some((item) => this.isEligible90DaySubscriptionItem(item, productId));
    }

    is90DayCartItem(item, config) {
      const eligibleIds = (config?.eligibleVariantIds || []).map(String);
      if (eligibleIds.length) {
        return eligibleIds.includes(String(item?.variant_id));
      }

      const haystack = [
        item?.variant_title,
        item?.sku,
        ...(item?.options || []),
        ...((item?.options_with_values || []).map((option) => option?.value)),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes('90');
    }

    isEligible90DaySubscriptionItem(item, productId) {
      if (String(item?.product_id) !== String(productId)) return false;
      if (!this.getSellingPlanId(item)) return false;
      return this.is90DayCartItem(item, this.configs.get(String(productId)));
    }

    is90DaySelection(form, payload) {
      const scope = form?.closest?.('[data-section-type="product"], [data-section-id]') || document;
      const widget = scope.querySelector('.ab-test-supply-selector-widget');
      const selected90 = widget?.querySelector(
        '[data-loop-supply-days="90"].loop-widget-purchase-option-selected, [data-loop-supply-days="90"][aria-checked="true"]',
      );
      if (selected90) return true;

      const variantId = String(payload?.get?.('id') || '');
      const config = this.getConfigForForm(form);
      if (variantId && config?.eligibleVariantIds?.includes(variantId)) return true;

      return false;
    }

    shouldAttachBonuses(form, payload) {
      if (!this.isEnabled()) return false;
      if (!this.formHasSellingPlan(form, payload)) return false;
      return this.is90DaySelection(form, payload);
    }

    getBonusLines(cart, productId) {
      return (cart?.items || []).filter((item) => {
        return this.getLineProperty(item, PROPERTY_KEY) === String(productId);
      });
    }

    collectOrphanBonusKeys(cart) {
      return (cart?.items || [])
        .filter((item) => {
          const parentProductId = this.getLineProperty(item, PROPERTY_KEY);
          if (!parentProductId) return false;
          return !this.hasActiveSubscription(cart, parentProductId);
        })
        .map((item) => item.key)
        .filter(Boolean);
    }

    getBonusKeysForRemovedLine(cart, lineKey) {
      const removedItem = (cart?.items || []).find((item) => item.key === lineKey);
      if (!removedItem) return [];

      const parentProductId = this.getLineProperty(removedItem, PROPERTY_KEY) || String(removedItem.product_id || '');
      if (!parentProductId) return [];

      const remainingHasSubscription = (cart?.items || []).some((item) => {
        return item.key !== lineKey && this.isEligible90DaySubscriptionItem(item, parentProductId);
      });
      if (remainingHasSubscription) return [];

      return this.getBonusLines(cart, parentProductId)
        .map((item) => item.key)
        .filter((key) => key && key !== lineKey);
    }

    async removeLineKeys(lineKeys, cart) {
      const uniqueKeys = [...new Set((lineKeys || []).filter(Boolean))];
      if (!uniqueKeys.length) return cart;

      if (typeof window.CartActionsV2.updateQuantities === 'function') {
        const updates = uniqueKeys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {});

        try {
          return await window.CartActionsV2.updateQuantities(updates, { dispatch: false });
        } catch (error) {
          console.error('Unable to remove cart lines in bulk', error);
        }
      }

      let workingCart = cart;
      for (const lineKey of uniqueKeys) {
        try {
          workingCart = await window.CartActionsV2.change(lineKey, 0, { dispatch: false });
        } catch (error) {
          // Line may already be gone after a previous change.
        }
      }

      return workingCart || window.CartActionsV2.get();
    }

    hasBonusVariant(cart, productId, variantId) {
      return (cart?.items || []).some((item) => {
        return String(item.variant_id) === String(variantId)
          && this.getLineProperty(item, PROPERTY_KEY) === String(productId);
      });
    }

    setLoading(isLoading) {
      document.body.classList.toggle('subscription-bonus-syncing', isLoading);

      document.querySelectorAll('cart-drawer-v2').forEach((drawer) => {
        drawer.classList.toggle('is-syncing-bonuses', isLoading);
        drawer.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      });

      document.querySelectorAll('.product__submit.is-syncing-bonuses-target').forEach((target) => {
        target.classList.toggle('is-syncing-bonuses', isLoading);
        target.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      });
    }

    registerLoadingTarget(element) {
      const target = element?.closest?.('.product__submit');
      if (!target) return;

      target.classList.add('is-syncing-bonuses-target');
    }

    getConfigForForm(form) {
      const scope = form?.closest?.('[data-section-type="product"]')
        || form?.closest?.('product-component')
        || form?.closest?.('[data-section-id]');
      const node = scope?.querySelector?.('[data-subscription-bonus-config]')
        || document.querySelector('[data-subscription-bonus-config]');
      if (!node) return null;

      try {
        const config = JSON.parse(node.textContent);
        if (!config?.productId || !config?.bonusVariantIds?.length) return null;

        return {
          productId: String(config.productId),
          bonusVariantIds: config.bonusVariantIds.map((id) => String(id)).filter(Boolean),
          eligibleVariantIds: (config.eligibleVariantIds || []).map((id) => String(id)).filter(Boolean),
        };
      } catch (error) {
        return null;
      }
    }

    buildMainItemFromFormData(payload) {
      const variantId = payload.get('id');
      if (!variantId) return null;

      const item = {
        id: variantId,
        quantity: Number.parseInt(String(payload.get('quantity') || '1'), 10) || 1,
      };

      const sellingPlan = payload.get('selling_plan');
      if (sellingPlan) item.selling_plan = sellingPlan;

      const properties = {};
      payload.forEach((value, key) => {
        const match = String(key).match(/^properties\[(.+)\]$/);
        if (match && value !== '') properties[match[1]] = value;
      });

      if (Object.keys(properties).length) {
        item.properties = properties;
      }

      return item;
    }

    buildSubscriptionAddItems(form, payload) {
      if (!this.shouldAttachBonuses(form, payload)) return null;

      const config = this.getConfigForForm(form);
      if (!config) return null;

      const mainItem = this.buildMainItemFromFormData(payload);
      if (!mainItem) return null;

      const bonusItems = config.bonusVariantIds.map((variantId) => ({
        id: variantId,
        quantity: 1,
        properties: {
          [PROPERTY_KEY]: String(config.productId),
        },
      }));

      return [mainItem, ...bonusItems];
    }

    normalizeLineProperties(item) {
      const properties = item?.properties;
      if (!properties) return {};

      if (Array.isArray(properties)) {
        return properties.reduce((acc, entry) => {
          if (Array.isArray(entry) && entry[0]) {
            acc[entry[0]] = entry[1];
          } else if (entry?.name) {
            acc[entry.name] = entry.value;
          }

          return acc;
        }, {});
      }

      if (typeof properties === 'object') {
        return { ...properties };
      }

      return {};
    }

    getParentSubscriptionLine(cart, productId) {
      return (cart?.items || []).find((item) => this.isEligible90DaySubscriptionItem(item, productId));
    }

    buildSyncPlan(cart, options = {}) {
      const { skipAttach = false } = options;
      const removeKeys = new Set(this.collectOrphanBonusKeys(cart));
      const itemsToAdd = [];

      if (!skipAttach) {
        for (const config of this.getConfigs()) {
          const productId = String(config.productId);
          if (!this.hasActiveSubscription(cart, productId)) continue;

          for (const variantId of config.bonusVariantIds) {
            if (this.hasBonusVariant(cart, productId, variantId)) continue;

            itemsToAdd.push({
              id: variantId,
              quantity: 1,
              properties: {
                [PROPERTY_KEY]: productId,
              },
            });
          }
        }
      }

      return {
        removeKeys: [...removeKeys],
        itemsToAdd,
        needsOrderFix: false,
        hasWork: removeKeys.size > 0 || itemsToAdd.length > 0,
      };
    }

    async syncCart(cart, options = {}) {
      if (!window.CartActionsV2 || !cart) return cart;

      const canAttach = this.isEnabled();
      const skipAttach = options.skipAttach === true || !canAttach;
      const showLoading = options.showLoading !== false;

      if (this.syncing) {
        const leftoverKeys = this.collectOrphanBonusKeys(cart);
        if (!leftoverKeys.length) return cart;
        return this.removeLineKeys(leftoverKeys, cart);
      }

      const plan = this.buildSyncPlan(cart, { skipAttach });
      if (!plan.hasWork) return cart;

      this.syncing = true;
      if (showLoading) this.setLoading(true);
      let workingCart = cart;

      try {
        if (plan.removeKeys.length) {
          workingCart = await this.removeLineKeys(plan.removeKeys, workingCart);
        }

        if (plan.itemsToAdd.length) {
          await window.CartActionsV2.addItems(plan.itemsToAdd, { dispatch: false });
          workingCart = await window.CartActionsV2.get();
        }

        const leftoverKeys = this.collectOrphanBonusKeys(workingCart);
        if (leftoverKeys.length) {
          workingCart = await this.removeLineKeys(leftoverKeys, workingCart);
        }

        document.dispatchEvent(
          new CustomEvent('cart-drawer-v2:updated', {
            bubbles: true,
            detail: { cart: workingCart },
          }),
        );

        return workingCart;
      } finally {
        this.syncing = false;
        if (showLoading) this.setLoading(false);
      }
    }

    formHasSellingPlan(form, payload) {
      if (!(form instanceof HTMLFormElement)) return false;

      const data = payload instanceof FormData ? payload : new FormData(form);
      if (data.get('selling_plan')) return true;

      const scope = form.closest('[data-section-type="product"], [data-section-id]') || document;
      const checkedPlan = scope.querySelector('[name="selling_plan"]:checked:not(:disabled)');
      return Boolean(checkedPlan?.value);
    }

    bindCartPageRemove() {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-item-remove]');
        if (!trigger) return;
        if (trigger.closest('cart-drawer-v2')) return;
        if (trigger.classList.contains('is-disabled')) return;

        const lineKey = trigger.dataset.id;
        if (!lineKey) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.handleCartPageRemove(trigger, lineKey);
      }, true);

      document.addEventListener('theme:cart:update', (event) => {
        const quantity = Number.parseInt(String(event.detail?.quantity ?? ''), 10);
        const lineKey = event.detail?.id;
        if (!lineKey || quantity !== 0) return;

        event.stopPropagation();
        this.handleCartPageRemove(event.target?.closest?.('[data-item]'), lineKey);
      }, true);
    }

    async handleCartPageRemove(trigger, lineKey) {
      const item = trigger instanceof Element
        ? trigger.closest('[data-item]')
        : trigger;
      const cartPage = document.querySelector('[data-cart-page], [data-cart-form]');

      item?.classList.add('is-removed');
      cartPage?.classList.add('is-loading');

      try {
        if (!window.CartActionsV2?.get) {
          throw new Error('Cart actions are not available');
        }

        const cart = await window.CartActionsV2.get();
        const bonusKeys = this.getBonusKeysForRemovedLine(cart, lineKey);

        bonusKeys.forEach((bonusKey) => {
          document.querySelector(`[data-item="${bonusKey}"]`)?.classList.add('is-removed');
        });

        await this.removeLineKeys([lineKey, ...bonusKeys], cart);
        const nextCart = await window.CartActionsV2.get();
        await this.syncCart(nextCart, { skipOrderFix: true });

        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      } catch (error) {
        console.error('Unable to remove cart item with bonus products', error);

        const fallbackHref = trigger instanceof Element ? trigger.getAttribute('href') : '';
        if (fallbackHref) {
          window.location.href = fallbackHref;
          return;
        }

        cartPage?.classList.remove('is-loading');
      }
    }
  }

  window.SubscriptionBonusProducts = new SubscriptionBonusProducts();
})();
